import type { VoyageReportKind } from './schemas.js';

/**
 * Bir seferin raporlarının sırası (ADR 0022 §1; müşterinin kuralları 1–11): Departure seferin ilk
 * akış raporudur ya da ara limanda Berthing'den hemen sonra gelir (her liman için kalkış; ADR
 * 0058 §4); bütün raporlar ilk Departure'dan sonra; seyir (COSP → EOSP), boğaz, kanal, demirleme, sürüklenme ve bekleme
 * başlangıç–bitiş çiftleridir; Noon açık seyirde, demirdeki günlük rapor açık demirlemede, drift
 * günlüğü açık sürüklenmede, limandaki günlük Berthing'den sonra girilir (ADR 0059 §2).
 * Saf fonksiyon: taslak ve gönderilmiş, silinmemiş raporların tamamı aday raporla birlikte
 * verilir; bütün dizi denetlenir, geriye dönük eklenen rapor sonrakileri bozamaz.
 *
 * Seferin ortasından başlama (ortağın notu #2; ADR 0047 §2): Departure yoksa geminin başlangıç
 * durumu bilinmez ve ilk rapordan çıkarılır. Bir çiftin ilk raporu bitişse o çift baştan açık
 * sayılır; seyir görülmeden gelen Noon seyri, demirleme görülmeden gelen günlük demir raporu
 * demirlemeyi açık sayar. Çift bir kez görüldükten sonra kurallar aynen işler. Departure varsa
 * çıkarım yapılmaz.
 */

export interface SequenceReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  data: Readonly<Record<string, unknown>>;
}

export interface SequenceIssue {
  id: string;
  code:
    | 'DUPLICATE_DEPARTURE'
    | 'BEFORE_DEPARTURE'
    | 'ALREADY_OPEN'
    | 'NOT_OPEN'
    | 'NAME_MISMATCH'
    | 'NOT_AT_SEA'
    | 'NOT_AT_ANCHOR'
    // Seferin günlükleri (ADR 0059 §2): limanda ve sürüklenmede
    | 'NOT_AT_BERTH'
    | 'NOT_DRIFTING'
    // Next Operation kilidi (ADR 0057 §5; `next-operation.ts`)
    | 'NEXT_OPERATION';
}

/** Başlangıç–bitiş çiftleri: seyir (COSP–EOSP), boğaz, kanal, demir, sürüklenme, bekleme. */
export type Family = 'passage' | 'strait' | 'canal' | 'anchorage' | 'drift' | 'waiting';

export const STARTS: Partial<Record<VoyageReportKind, Family>> = {
  cosp: 'passage',
  strait_entry: 'strait',
  canal_entry: 'canal',
  anchorage_start: 'anchorage',
  drift_start: 'drift',
  waiting_start: 'waiting',
};
export const ENDS: Partial<Record<VoyageReportKind, Family>> = {
  eosp: 'passage',
  strait_exit: 'strait',
  canal_exit: 'canal',
  anchorage_end: 'anchorage',
  drift_end: 'drift',
  waiting_end: 'waiting',
};
/** Adı eşleşmesi gereken çiftler (giriş ve çıkış aynı boğaz ya da kanal). */
const NAMED: ReadonlySet<Family> = new Set(['strait', 'canal']);

/**
 * Zamana göre sıra. Aynı andaki raporlarda önce Departure, sonra açık bir çifti kapatan bitişler,
 * sonra başlangıçlar, sonra diğerleri, en son kalan bitişler: aynı anda bitip yeniden başlayan da,
 * aynı anda başlayıp biten de geçerlidir.
 */
function* ordered(
  reports: readonly SequenceReport[],
  open: ReadonlyMap<Family, string>,
): Generator<SequenceReport> {
  const groups = new Map<number, SequenceReport[]>();
  for (const report of reports) {
    const at = Date.parse(report.reportedAt);
    groups.set(at, [...(groups.get(at) ?? []), report]);
  }
  for (const at of [...groups.keys()].sort((a, b) => a - b)) {
    const pending = [...(groups.get(at) ?? [])];
    const take = (match: (r: SequenceReport) => boolean) => {
      const index = pending.findIndex(match);
      return index < 0 ? undefined : pending.splice(index, 1)[0];
    };
    const phases: ((r: SequenceReport) => boolean)[] = [
      (r) => r.kind === 'departure',
      (r) => {
        const family = ENDS[r.kind];
        return family !== undefined && open.has(family);
      },
      (r) => STARTS[r.kind] !== undefined,
      (r) => ENDS[r.kind] === undefined,
      () => true,
    ];
    for (const phase of phases) {
      for (let next = take(phase); next; next = take(phase)) yield next;
    }
  }
}

/** Akışın dışındaki olaylar (yük, kiralama): ara limandan çıkış kuralında sayılmaz. */
const SIDE: ReadonlySet<VoyageReportKind> = new Set([
  'cargo_start',
  'cargo_complete',
  'on_hire',
  'off_hire',
]);

/**
 * İlk Departure'dan önceki raporlar seferin ortasından başlangıç mı (ADR 0047 §2, ADR 0058 §4):
 * önlerinde akış raporu yoksa ya da sonuncusu Berthing ise evet.
 */
function prefixAllowed(reports: readonly SequenceReport[], first: string): boolean {
  const before = reports
    .filter(
      (r) =>
        !SIDE.has(r.kind) &&
        r.kind !== 'port_daily' &&
        Date.parse(r.reportedAt) < Date.parse(first),
    )
    .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt));
  return before.length === 0 || before.at(-1)?.kind === 'arrival';
}

/**
 * `recover`: hatalı raporun çift durumuna etkisi yine uygulanır (`replay` gibi): seyir açılmadan
 * gelen Noon seyri açar, adı tutmayan çıkış çifti kapatır. Böylece her hata yereldir, eski bir
 * çelişki sonraki raporlara zincirlenmez. Geminin zaman çizgisinde yalnız yeni çelişkiyi ayırmak
 * için (ADR 0083 §1); varsayılan davranış değişmez.
 */
export function checkReportSequence(
  reports: readonly SequenceReport[],
  options: { recover?: boolean } = {},
): SequenceIssue[] {
  const recover = options.recover === true;
  const issues: SequenceIssue[] = [];
  const departures = reports.filter((r) => r.kind === 'departure');
  const first = departures
    .map((r) => r.reportedAt)
    .sort((a, b) => Date.parse(a) - Date.parse(b))[0];
  // Limanda biten önek seferin ortasından başlangıçtır; değilse ilk Departure'dan önceki rapor hata
  const midVoyagePrefix = first !== undefined && prefixAllowed(reports, first);
  const open = new Map<Family, string>();
  const seen = new Set<Family>();
  let departureSeen = false;
  // Departure'sız seferde ya da ilk Departure'dan önceki önekte başlangıç durumu çıkarılır
  const implicitlyOpen = (family: Family) => !departureSeen && !seen.has(family);
  let lastFlow: VoyageReportKind | null = null;
  for (const report of ordered(reports, open)) {
    if (report.kind === 'departure') {
      // Her liman için kalkış (ADR 0058 §4): ilk akış raporu ya da Berthing'den hemen sonra
      if (departureSeen && lastFlow !== 'arrival') {
        issues.push({ id: report.id, code: 'DUPLICATE_DEPARTURE' });
      }
      departureSeen = true;
      lastFlow = 'departure';
      continue;
    }
    if (first && !midVoyagePrefix && Date.parse(report.reportedAt) < Date.parse(first)) {
      issues.push({ id: report.id, code: 'BEFORE_DEPARTURE' });
      continue;
    }
    if (report.kind === 'port_daily') {
      // Limanda günlük (ADR 0059 §2): son akış raporu Berthing ya da limanda başlayan seferin
      // başı; kalkış kuralı (Berthing'den hemen sonra) bu raporu atlar
      const atBerth = lastFlow === 'arrival' || (lastFlow === null && !departureSeen);
      if (!atBerth) issues.push({ id: report.id, code: 'NOT_AT_BERTH' });
      continue;
    }
    if (!SIDE.has(report.kind)) lastFlow = report.kind;
    const name = typeof report.data.name === 'string' ? report.data.name.trim() : '';
    const starts = STARTS[report.kind];
    const ends = ENDS[report.kind];
    if (starts) {
      seen.add(starts);
      if (open.has(starts)) issues.push({ id: report.id, code: 'ALREADY_OPEN' });
      else open.set(starts, name);
    } else if (ends) {
      const opened = open.get(ends);
      if (opened === undefined) {
        // Baştan açık sayılan çiftin bitişi: adı bilinmez, denetlenmez
        if (!implicitlyOpen(ends)) issues.push({ id: report.id, code: 'NOT_OPEN' });
      } else if (NAMED.has(ends) && opened.toLowerCase() !== name.toLowerCase()) {
        issues.push({ id: report.id, code: 'NAME_MISMATCH' });
        if (recover) open.delete(ends);
      } else open.delete(ends);
      seen.add(ends);
    } else if (report.kind === 'noon' && !open.has('passage')) {
      if (implicitlyOpen('passage')) {
        open.set('passage', '');
        seen.add('passage');
      } else {
        issues.push({ id: report.id, code: 'NOT_AT_SEA' });
        if (recover) open.set('passage', '');
      }
    } else if (report.kind === 'anchorage_daily' && !open.has('anchorage')) {
      if (implicitlyOpen('anchorage')) {
        open.set('anchorage', '');
        seen.add('anchorage');
      } else {
        issues.push({ id: report.id, code: 'NOT_AT_ANCHOR' });
        if (recover) open.set('anchorage', '');
      }
    } else if (report.kind === 'drift_daily' && !open.has('drift')) {
      if (implicitlyOpen('drift')) {
        open.set('drift', '');
        seen.add('drift');
      } else {
        issues.push({ id: report.id, code: 'NOT_DRIFTING' });
        if (recover) open.set('drift', '');
      }
    }
  }
  return issues;
}

/**
 * Raporlar `checkReportSequence` ile aynı sırada, her biri o andaki açık çiftlerle (rapordan
 * önceki durum). Hata denetlemez: bitiş açık olmayan çifti kapatmaz; seyir görülmeden gelen Noon
 * seyri, demir görülmeden gelen günlük demir raporu demirlemeyi açık sayar (ADR 0047 §2). Next
 * Operation kilidi (ADR 0057 §5) bunu kullanır.
 */
export function* replay(
  reports: readonly SequenceReport[],
): Generator<{ report: SequenceReport; open: ReadonlySet<Family> }> {
  const open = new Map<Family, string>();
  for (const report of ordered(reports, open)) {
    yield { report, open: new Set(open.keys()) };
    const starts = STARTS[report.kind];
    const ends = ENDS[report.kind];
    if (starts) open.set(starts, '');
    else if (ends) open.delete(ends);
    else if (report.kind === 'noon') open.set('passage', '');
    else if (report.kind === 'anchorage_daily') open.set('anchorage', '');
    else if (report.kind === 'drift_daily') open.set('drift', '');
  }
}
